import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { crearAsientoManual } from "../hooks/useAsientos";
import { CuentaSelectSearch } from "../../catalogo/components/CuentaSelect";
import { useMultimoneda } from "../../../../shared/hooks/useMultimoneda";
import { useTipoCambio } from "../../services/useTipoCambio";
import styles from "../../invoices/styles/addInvoice.module.css";

const D      = (n) => Number(n || 0);
const r2     = (n) => Math.round(Number(n || 0) * 100) / 100;
const fmt    = (n) => D(n).toLocaleString("en-US", { minimumFractionDigits: 2 });
const fmtUSD = (n) => D(n).toLocaleString("en-US", { minimumFractionDigits: 2 });

// Agrega separador de miles a un string numérico crudo (sin comas), preservando
// lo que el usuario está escribiendo (parte decimal incluida).
function conComas(raw) {
  if (raw === "" || raw == null) return "";
  const [entero, decimales] = String(raw).split(".");
  const conMiles = entero.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  return decimales !== undefined ? `${conMiles}.${decimales}` : conMiles;
}

/**
 * MoneyInput — input de monto con separador de miles (000,000.00).
 * Mientras el campo tiene foco muestra el valor crudo (más fácil de editar);
 * al perder el foco lo muestra formateado. El valor que se guarda en el
 * estado del formulario SIEMPRE es el string crudo sin comas.
 */
function MoneyInput({ value, onChange, style, ...props }) {
  const [focused, setFocused] = useState(false);
  const display = focused ? value : conComas(value);
  return (
    <input
      type="text" inputMode="decimal" placeholder="0.00"
      value={display}
      onFocus={() => setFocused(true)}
      onBlur={() => setFocused(false)}
      onChange={(e) => {
        let raw = e.target.value.replace(/[^\d.]/g, "");
        const partes = raw.split(".");
        if (partes.length > 2) raw = partes[0] + "." + partes.slice(1).join("");
        onChange(raw);
      }}
      style={style}
      {...props}
    />
  );
}

// El CSS compartido de la tarjeta izquierda fuerza `border` con !important en
// todos los <input> anidados (para que Fecha/Concepto luzcan igual que en el
// resto de la app) — eso anularía el borde de color que distingue Debe/Haber
// por línea. Se usa box-shadow (que el CSS compartido no fuerza) para ese
// detalle en vez de border-color.
function montoRing(hasErr, tint) {
  if (hasErr) return "inset 0 0 0 1.5px #ef4444";
  if (tint)   return `inset 0 0 0 1.5px ${tint}`;
  return "none";
}

const LINEA_VACIA = () => ({
  _key:        Date.now() + Math.random(),
  cuenta:      null,
  cuentaObj:   null,
  debito:      "",
  credito:     "",
  debito_usd:  "",
  credito_usd: "",
  descripcion: "",
});

export default function AddAsiento() {
  const { t }       = useTranslation();
  const navigate    = useNavigate();
  const multimoneda = useMultimoneda();
  const today       = new Date(Date.now() - new Date().getTimezoneOffset() * 60000).toISOString().split("T")[0];

  const [form,   setForm]   = useState({ fecha: today, concepto: "" });
  const [lineas, setLineas] = useState([LINEA_VACIA(), LINEA_VACIA()]);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [apiErr, setApiErr] = useState("");

  // ── Tipo de cambio + auto-cálculo ₡ ⇄ $ ────────────────────────────────────
  // El usuario digita el monto en UNA sola moneda por línea y el sistema
  // calcula la otra con el TC, para no obligarlo a hacer la conversión a mano.
  const { tipoCambio: tcBccr } = useTipoCambio();
  const [tc,       setTc]       = useState(1);
  const [autoCalc, setAutoCalc] = useState(true);
  const tcNum = Number(tc) || 1;

  // Precarga silenciosa del TC del BCCR (compra) la primera vez que llega.
  useEffect(() => {
    if (multimoneda && tcBccr?.tipo_compra && tc === 1) {
      setTc(Number(tcBccr.tipo_compra));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [multimoneda, tcBccr]);

  // ── Totales ──────────────────────────────────────────────────────────────
  const totalD    = lineas.reduce((a, l) => a + D(l.debito),      0);
  const totalC    = lineas.reduce((a, l) => a + D(l.credito),     0);
  const totalDusd = lineas.reduce((a, l) => a + D(l.debito_usd),  0);
  const totalCusd = lineas.reduce((a, l) => a + D(l.credito_usd), 0);

  const diff    = Math.abs(totalD - totalC);
  const diffUsd = Math.abs(totalDusd - totalCusd);
  const hasUsd  = multimoneda && (totalDusd > 0 || totalCusd > 0);

  const crcOk = diff < 0.001 && totalD > 0;
  const usdOk = !hasUsd || diffUsd < 0.001;
  const ok    = crcOk && usdOk;

  // ── Mutadores ────────────────────────────────────────────────────────────
  function setLinea(key, field, value) {
    setLineas((p) => p.map((l) => l._key === key ? { ...l, [field]: value } : l));
  }
  function setCuenta(key, id, obj) {
    setLineas((p) => p.map((l) => l._key === key ? { ...l, cuenta: id, cuentaObj: obj } : l));
  }

  // Setter unificado para los 4 campos de monto de una línea (debito, credito,
  // debito_usd, credito_usd). Aplica dos reglas:
  //   1) Debe y Haber de la MISMA moneda son mutuamente excluyentes (escribir
  //      uno limpia el otro) — igual que antes.
  //   2) Si el auto-cálculo está activo y hay TC, la moneda contraria se
  //      calcula sola (₡ = $ × TC, $ = ₡ / TC) para que el usuario solo
  //      digite en una de las dos.
  function setMonto(key, field, rawValue) {
    setLineas((prev) => prev.map((l) => {
      if (l._key !== key) return l;
      const next = { ...l, [field]: rawValue };

      if (field === "debito")      next.credito     = "";
      if (field === "credito")     next.debito      = "";
      if (field === "debito_usd")  next.credito_usd = "";
      if (field === "credito_usd") next.debito_usd  = "";

      if (autoCalc && multimoneda && tcNum > 1) {
        const n = Number(rawValue) || 0;
        if (field === "debito") {
          next.debito_usd  = rawValue === "" ? "" : (n > 0 ? String(r2(n / tcNum)) : "");
          next.credito_usd = "";
        } else if (field === "credito") {
          next.credito_usd = rawValue === "" ? "" : (n > 0 ? String(r2(n / tcNum)) : "");
          next.debito_usd  = "";
        } else if (field === "debito_usd") {
          next.debito   = rawValue === "" ? "" : (n > 0 ? String(r2(n * tcNum)) : "");
          next.credito  = "";
        } else if (field === "credito_usd") {
          next.credito  = rawValue === "" ? "" : (n > 0 ? String(r2(n * tcNum)) : "");
          next.debito   = "";
        }
      }
      return next;
    }));
  }
  function agregarLinea()   { setLineas((p) => [...p, LINEA_VACIA()]); }
  function eliminarLinea(k) {
    setLineas((prev) => prev.length <= 2 ? prev : prev.filter((l) => l._key !== k));
  }
  function duplicarLinea(k) {
    setLineas((prev) => {
      const idx = prev.findIndex((l) => l._key === k);
      if (idx === -1) return prev;
      const copia = { ...prev[idx], _key: Date.now() + Math.random() };
      const next = [...prev];
      next.splice(idx + 1, 0, copia);
      return next;
    });
  }

  // ── Validación ───────────────────────────────────────────────────────────
  function validate() {
    const e = {};
    if (!form.fecha)           e.fecha    = t("common.requerido") || "Requerida.";
    if (!form.concepto.trim()) e.concepto = t("common.requerido") || "Requerido.";
    lineas.forEach((l, i) => {
      if (!l.cuenta)                             e[`l_cuenta_${i}`] = t("asientos.seleccionaCuenta");
      if (!D(l.debito) && !D(l.credito))        e[`l_monto_${i}`]  = t("asientos.ingresaDebitoCredito");
      if (D(l.debito) > 0 && D(l.credito) > 0) e[`l_ambos_${i}`]  = t("asientos.soloDebitoOCredito");
      if (multimoneda && D(l.debito_usd) > 0 && D(l.credito_usd) > 0)
        e[`l_ambos_usd_${i}`] = "USD: solo Debe o Haber por línea.";
    });
    if (!crcOk && !Object.keys(e).some((k) => !k.startsWith("l_")))
      e.balance = `${t("asientos.noBalanceadoDiff")}₡${fmt(diff)}`;
    if (hasUsd && !usdOk && !e.balance)
      e.balance_usd = `Asiento USD no balanceado — Diferencia: $${fmtUSD(diffUsd)}`;
    setErrors(e);
    return Object.keys(e).length === 0;
  }

  // ── Enviar ───────────────────────────────────────────────────────────────
  async function handleSubmit(ev) {
    ev.preventDefault();
    if (!validate()) return;
    setSaving(true);
    setApiErr("");
    try {
      await crearAsientoManual({
        fecha:    form.fecha,
        concepto: form.concepto.trim(),
        lineas:   lineas.map((l) => ({
          cuenta:      l.cuenta,
          debito:      D(l.debito)      || 0,
          credito:     D(l.credito)     || 0,
          debito_usd:  multimoneda ? (D(l.debito_usd)  || 0) : 0,
          credito_usd: multimoneda ? (D(l.credito_usd) || 0) : 0,
          moneda:      multimoneda && (D(l.debito_usd) > 0 || D(l.credito_usd) > 0) ? "USD" : "CRC",
          descripcion: l.descripcion || "",
        })),
      });
      navigate("/finanzas/asientos");
    } catch (e) {
      try {
        const p = JSON.parse(e.message);
        setApiErr(p.lineas?.join(", ") || p.non_field_errors?.join(", ") || JSON.stringify(p));
      } catch { setApiErr(e.message); }
    } finally { setSaving(false); }
  }

  // ── Estilos ──────────────────────────────────────────────────────────────
  const inputStyle = (err) => ({
    width: "100%", boxSizing: "border-box",
    padding: "8px 12px",
    background: "var(--bg-input)",
    border: `1px solid ${err ? "#ef4444" : "var(--border-input)"}`,
    borderRadius: 6,
    color: "var(--text-primary)",
    fontSize: 14,
    outline: "none",
    colorScheme: "light dark",
    transition: "0.3s",
  });

  const labelStyle = {
    display: "block", fontSize: 14, fontWeight: 500,
    color: "var(--text-primary)", marginBottom: 4,
  };

  const colHeaderStyle = {
    fontSize: 12, fontWeight: 600, color: "var(--text-secondary)",
    textTransform: "uppercase", letterSpacing: "0.4px", padding: "10px 12px",
  };

  const errTxt = (msg) => msg
    ? <p style={{ color: "#f87171", fontSize: 12, margin: "3px 0 0" }}>{msg}</p>
    : null;

  // Grid de la tabla de líneas — de nuevo a ancho completo (una sola columna
  // de página, sin panel lateral), con la Naturaleza como columna propia y
  // acciones (duplicar/eliminar) al final.
  const GRID = multimoneda
    ? "minmax(200px,1.6fr) minmax(110px,1fr) 145px 145px 140px 140px 100px 74px"
    : "minmax(240px,2fr) minmax(160px,1fr) 150px 150px 100px 74px";

  const cardStyle = {
    background: "var(--bg-card)", border: "1px solid var(--border-card)",
    borderRadius: 12, marginBottom: 16, overflow: "visible",
  };

  function CardHeader({ icon, iconBg, iconColor, title, subtitle }) {
    return (
      <div style={{ padding: "16px 20px", borderBottom: "1px solid var(--border-card)",
        display: "flex", alignItems: "center", gap: 12 }}>
        <div style={{ width: 40, height: 40, borderRadius: 10, flexShrink: 0,
          background: iconBg, color: iconColor,
          display: "flex", alignItems: "center", justifyContent: "center", fontSize: 16 }}>
          <i className={`fa-solid ${icon}`}/>
        </div>
        <div>
          <div style={{ fontWeight: 700, fontSize: 15, color: "var(--text-primary)" }}>{title}</div>
          {subtitle && <div style={{ fontSize: 12.5, color: "var(--text-muted)", marginTop: 1 }}>{subtitle}</div>}
        </div>
      </div>
    );
  }

  return (
    <>
      {/* Animación de entrada para líneas nuevas */}
      <style>{`
        @keyframes asientoLineaIn { from { opacity:0; transform:translateY(-6px); } to { opacity:1; transform:none; } }
      `}</style>

      {/* Breadcrumb */}
      <span className={styles.inactive}>
        <Link to="/"><span className={styles.inactive}>{t("common.inicio")} /</span></Link>
        <Link to="/finanzas/asientos"><span className={styles.inactive}> {t("asientos.titulo")} /</span></Link>
        <span className={styles.active}> {t("asientos.nuevoAsientoManual")}</span>
      </span>

      <h2 style={{ fontSize: 20, fontWeight: 600, color: "var(--text-primary)", margin: "12px 0 20px",
        display: "flex", alignItems: "center", gap: 8 }}>
        <span style={{ background: "rgba(249,115,22,0.2)", color: "#f97316", padding: "6px 8px", borderRadius: 8 }}>
          <i className="fa-solid fa-book-open" style={{ color: "#f97316" }} />
        </span>
        {t("asientos.nuevoAsientoManual")}
        {multimoneda && (
          <span style={{ fontSize: 11, fontWeight: 700, padding: "2px 8px", borderRadius: 4,
            background: "rgba(96,165,250,0.12)", color: "#60a5fa",
            border: "1px solid rgba(96,165,250,0.25)", letterSpacing: "0.4px" }}>
            CRC ↔ USD
          </span>
        )}
      </h2>

      {/* ── Errores API ── */}
      {apiErr && (
        <div style={{
          padding: "10px 14px", borderRadius: 6, fontSize: 14, marginBottom: 14,
          background: "rgba(239,68,68,0.07)", border: "1px solid rgba(239,68,68,0.3)",
          color: "#f87171", display: "flex", alignItems: "center", gap: 8,
        }}>
          <i className="fa-solid fa-circle-exclamation" />{apiErr}
        </div>
      )}

      <form onSubmit={handleSubmit}>

        {/* ── Encabezado ── */}
        <div style={cardStyle}>
          <CardHeader icon="fa-book-open" iconBg="rgba(249,115,22,0.18)" iconColor="#f97316"
            title={t("asientos.encabezado")} subtitle={t("asientos.encabezadoDesc")} />
          <div style={{ padding: 20, display: "flex", flexDirection: "column", gap: 16 }}>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
              <div>
                <label style={labelStyle}>{t("common.fecha")} <span style={{ color: "#f87171" }}>*</span></label>
                <input type="date" style={inputStyle(errors.fecha)}
                  value={form.fecha}
                  onChange={(e) => setForm((p) => ({ ...p, fecha: e.target.value }))} />
                {errTxt(errors.fecha)}
              </div>
              <div>
                <label style={labelStyle}>{t("asientos.concepto")} <span style={{ color: "#f87171" }}>*</span></label>
                <input type="text" style={inputStyle(errors.concepto)}
                  placeholder={t("asientos.conceptoPlaceholder")}
                  value={form.concepto}
                  onChange={(e) => setForm((p) => ({ ...p, concepto: e.target.value }))} />
                {errTxt(errors.concepto)}
              </div>
            </div>

            {/* Tipo de cambio + auto-cálculo ₡ ⇄ $ — solo con multimoneda */}
            {multimoneda && (
              <div style={{ display: "flex", alignItems: "center", gap: 16, flexWrap: "wrap",
                paddingTop: 16, borderTop: "1px solid var(--border-card)" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <span style={{ fontSize: 13, color: "var(--text-muted)" }}>{t("traslados.tipoCambio")}:</span>
                  <div style={{ display: "flex", alignItems: "center", gap: 4,
                    background: "var(--bg-input)", border: "1px solid var(--border-input)",
                    borderRadius: 7, padding: "6px 10px" }}>
                    <span style={{ fontSize: 13, color: "var(--text-muted)", fontWeight: 600 }}>₡</span>
                    <input type="number" step="0.01" min="1.01"
                      value={tc}
                      onChange={(e) => setTc(parseFloat(e.target.value) || 1)}
                      style={{ width: 80, background: "transparent", border: "none",
                        color: "var(--text-primary)", fontSize: 14, fontFamily: "monospace",
                        fontWeight: 600, outline: "none", padding: 0 }}/>
                    <span style={{ fontSize: 12, color: "var(--text-muted)" }}>/ USD</span>
                  </div>
                </div>

                <button type="button" onClick={() => setAutoCalc((v) => !v)}
                  title={t("asientos.autoCalcHint")}
                  style={{
                    display: "flex", alignItems: "center", gap: 7, padding: "6px 12px",
                    borderRadius: 20, cursor: "pointer",
                    background: autoCalc ? "rgba(74,222,128,0.15)" : "var(--hover-bg)",
                    border: `1px solid ${autoCalc ? "rgba(74,222,128,0.4)" : "var(--border-card)"}`,
                    color: autoCalc ? "#4ade80" : "var(--text-muted)",
                    fontSize: 13, fontWeight: 600, transition: "all 0.15s",
                  }}>
                  <i className={`fa-solid ${autoCalc ? "fa-toggle-on" : "fa-toggle-off"}`} style={{ fontSize: 15 }}/>
                  {t("asientos.autoCalc")}
                </button>
                <span style={{ fontSize: 12, color: "var(--text-muted)", flex: "1 1 220px" }}>
                  {t("asientos.autoCalcHint")}
                </span>
              </div>
            )}
          </div>
        </div>

        {/* ── Detalle del asiento ── */}
        <div style={cardStyle}>
          <CardHeader icon="fa-diagram-project" iconBg="rgba(96,165,250,0.18)" iconColor="#60a5fa"
            title={t("asientos.detalleAsiento")} subtitle={t("asientos.detalleAsientoDesc")} />

          <div style={{ overflowX: "auto" }}>
            {/* Encabezado columnas */}
            <div style={{ display: "grid", gridTemplateColumns: `36px ${GRID}`,
              background: "var(--hover-bg)", borderBottom: "1px solid var(--border-card)",
              minWidth: 1100 }}>
              <span />
              <span style={colHeaderStyle}>{t("asientos.cuentaContable")}</span>
              <span style={colHeaderStyle}>{t("common.descripcion")}</span>
              <span style={{ ...colHeaderStyle, textAlign: "right", color: "#4ade80" }}>
                {t("asientos.debeCol")} ₡
              </span>
              <span style={{ ...colHeaderStyle, textAlign: "right", color: "#60a5fa" }}>
                {t("asientos.haberCol")} ₡
              </span>
              {multimoneda && <>
                <span style={{ ...colHeaderStyle, textAlign: "right", color: "#86efac",
                  borderLeft: "1px dashed var(--border-card)" }}>
                  {t("asientos.debeCol")} $
                </span>
                <span style={{ ...colHeaderStyle, textAlign: "right", color: "#93c5fd" }}>
                  {t("asientos.haberCol")} $
                </span>
              </>}
              <span style={colHeaderStyle}>{t("asientos.naturaleza")}</span>
              <span />
            </div>

            {/* Filas */}
            {lineas.map((l, i) => {
              const nat         = l.cuentaObj?.naturaleza;
              const sugiereHaber = nat === "ACREEDORA";
              const sugiereDebe  = nat === "DEUDORA";
              const enContraNat  = (sugiereDebe && Number(l.credito) > 0) ||
                                   (sugiereHaber && Number(l.debito)  > 0);
              const hasLineErr   = errors[`l_monto_${i}`] || errors[`l_ambos_${i}`];
              const hasUsdErr    = errors[`l_ambos_usd_${i}`];

              return (
                <div key={l._key} style={{
                  display: "grid", gridTemplateColumns: `36px ${GRID}`,
                  gap: 8, padding: "10px 12px", minWidth: 1100,
                  background: i % 2 === 0 ? "transparent" : "var(--hover-bg)",
                  borderBottom: "1px solid var(--border-card)",
                  alignItems: "start", overflow: "visible", position: "relative",
                  zIndex: lineas.length - i,
                  animation: "asientoLineaIn 0.2s ease",
                }}>
                  {/* Número de línea */}
                  <span style={{
                    width: 26, height: 26, borderRadius: 8, marginTop: 3,
                    background: "var(--hover-bg-strong)", color: "var(--text-muted)",
                    fontSize: 11, fontWeight: 700, display: "flex", alignItems: "center",
                    justifyContent: "center", flexShrink: 0,
                  }}>
                    {i + 1}
                  </span>

                  {/* Cuenta */}
                  <div style={{ overflow: "visible" }}>
                    <CuentaSelectSearch
                      value={l.cuenta}
                      onChange={(id, obj) => setCuenta(l._key, id, obj)}
                      placeholder={t("asientos.buscarCuenta")}
                      error={errors[`l_cuenta_${i}`]}
                    />
                  </div>

                  {/* Descripción */}
                  <input type="text" placeholder={t("common.opcional")}
                    style={inputStyle(false)}
                    value={l.descripcion}
                    onChange={(e) => setLinea(l._key, "descripcion", e.target.value)}
                  />

                  {/* Debe ₡ */}
                  <MoneyInput
                    style={{
                      ...inputStyle(false),
                      textAlign: "right", fontFamily: "monospace",
                      boxShadow: montoRing(hasLineErr, l.debito
                        ? (enContraNat && sugiereHaber ? "rgba(251,146,60,0.6)" : "rgba(248,113,113,0.55)")
                        : null),
                    }}
                    value={l.debito}
                    onChange={(v) => setMonto(l._key, "debito", v)}
                  />

                  {/* Haber ₡ */}
                  <MoneyInput
                    style={{
                      ...inputStyle(false),
                      textAlign: "right", fontFamily: "monospace",
                      boxShadow: montoRing(hasLineErr, l.credito
                        ? (enContraNat && sugiereDebe ? "rgba(251,146,60,0.6)" : "rgba(96,165,250,0.55)")
                        : null),
                    }}
                    value={l.credito}
                    onChange={(v) => setMonto(l._key, "credito", v)}
                  />

                  {/* Debe $ / Haber $ — solo cuando multimoneda */}
                  {multimoneda && (
                    <>
                      <MoneyInput
                        style={{
                          ...inputStyle(false),
                          textAlign: "right", fontFamily: "monospace",
                          borderLeft: "1px dashed var(--border-card)",
                          boxShadow: montoRing(hasUsdErr, l.debito_usd ? "rgba(134,239,172,0.55)" : null),
                        }}
                        value={l.debito_usd}
                        onChange={(v) => setMonto(l._key, "debito_usd", v)}
                      />
                      <MoneyInput
                        style={{
                          ...inputStyle(false),
                          textAlign: "right", fontFamily: "monospace",
                          boxShadow: montoRing(hasUsdErr, l.credito_usd ? "rgba(147,197,253,0.55)" : null),
                        }}
                        value={l.credito_usd}
                        onChange={(v) => setMonto(l._key, "credito_usd", v)}
                      />
                    </>
                  )}

                  {/* Naturaleza — columna propia */}
                  <div style={{ display: "flex", alignItems: "center", height: 34 }}>
                    {nat ? (
                      <span style={{
                        display: "inline-flex", alignItems: "center", gap: 4,
                        fontSize: 10.5, fontWeight: 700,
                        padding: "3px 8px", borderRadius: 5,
                        background: sugiereDebe ? "rgba(248,113,113,0.12)" : "rgba(96,165,250,0.12)",
                        color: sugiereDebe ? "#f87171" : "#60a5fa",
                        border: `1px solid ${sugiereDebe ? "rgba(248,113,113,0.25)" : "rgba(96,165,250,0.25)"}`,
                        letterSpacing: "0.3px", whiteSpace: "nowrap",
                      }}>
                        <i className={`fa-solid ${sugiereDebe ? "fa-arrow-up" : "fa-arrow-left"}`}
                           style={{ fontSize: 8 }}/>
                        {sugiereDebe ? t("asientos.naturalDebe") : t("asientos.naturalHaber")}
                        {enContraNat && (
                          <span style={{ color: "#fb923c", marginLeft: 2 }} title="Usando contra-naturaleza">⚡</span>
                        )}
                      </span>
                    ) : <span style={{ color: "var(--text-muted)", fontSize: 12 }}>—</span>}
                  </div>

                  {/* Duplicar + Eliminar */}
                  <div style={{ display: "flex", gap: 6, alignItems: "center", height: 34 }}>
                    <button type="button" onClick={() => duplicarLinea(l._key)}
                      title={t("asientos.duplicarLinea")}
                      style={{
                        width: 28, height: 28, borderRadius: 7, flexShrink: 0,
                        background: "var(--hover-bg)", border: "1px solid var(--border-card)",
                        color: "var(--text-secondary)", cursor: "pointer",
                        fontSize: 12, transition: "all 0.15s",
                        display: "flex", alignItems: "center", justifyContent: "center",
                      }}
                      onMouseOver={(e) => { e.currentTarget.style.color = "#60a5fa"; e.currentTarget.style.borderColor = "rgba(96,165,250,0.4)"; }}
                      onMouseOut={(e)  => { e.currentTarget.style.color = "var(--text-secondary)"; e.currentTarget.style.borderColor = "var(--border-card)"; }}
                    >
                      <i className="fa-regular fa-copy" />
                    </button>
                    <button type="button" onClick={() => eliminarLinea(l._key)}
                      disabled={lineas.length <= 2}
                      title={t("common.eliminar")}
                      style={{
                        width: 28, height: 28, borderRadius: 7, flexShrink: 0,
                        background: lineas.length <= 2 ? "var(--hover-bg)" : "rgba(239,68,68,0.08)",
                        border: `1px solid ${lineas.length <= 2 ? "var(--border-card)" : "rgba(239,68,68,0.25)"}`,
                        color: lineas.length <= 2 ? "var(--border-card)" : "#f87171",
                        cursor: lineas.length <= 2 ? "default" : "pointer",
                        fontSize: 12, transition: "all 0.15s",
                        display: "flex", alignItems: "center", justifyContent: "center",
                      }}
                      onMouseOver={(e) => { if (lineas.length > 2) e.currentTarget.style.background = "rgba(239,68,68,0.18)"; }}
                      onMouseOut={(e)  => { if (lineas.length > 2) e.currentTarget.style.background = "rgba(239,68,68,0.08)"; }}
                    >
                      <i className="fa-solid fa-trash" />
                    </button>
                  </div>
                </div>
              );
            })}

            {/* Totales */}
            <div style={{
              display: "grid", gridTemplateColumns: `36px ${GRID}`,
              gap: 8, padding: "10px 12px", minWidth: 1100,
              background: "var(--hover-bg)", borderTop: "2px solid var(--border-card)",
              alignItems: "center",
            }}>
              <span />
              <span style={{ fontSize: 12, fontWeight: 700, color: "var(--text-secondary)",
                textTransform: "uppercase", letterSpacing: "0.4px" }}>
                {t("asientos.totales").toUpperCase()}
              </span>
              <span />
              <span style={{ textAlign: "right", fontFamily: "monospace",
                fontWeight: 700, fontSize: 14, color: "#4ade80" }}>
                ₡{fmt(totalD)}
              </span>
              <span style={{ textAlign: "right", fontFamily: "monospace",
                fontWeight: 700, fontSize: 14, color: "#60a5fa" }}>
                ₡{fmt(totalC)}
              </span>
              {multimoneda && (
                <>
                  <span style={{ textAlign: "right", fontFamily: "monospace",
                    fontWeight: 700, fontSize: 14, color: "#86efac",
                    borderLeft: "1px dashed var(--border-card)", paddingLeft: 8 }}>
                    ${fmtUSD(totalDusd)}
                  </span>
                  <span style={{ textAlign: "right", fontFamily: "monospace",
                    fontWeight: 700, fontSize: 14, color: "#93c5fd" }}>
                    ${fmtUSD(totalCusd)}
                  </span>
                </>
              )}
              <span />
              <span />
            </div>

            {/* Agregar línea */}
            <div style={{ padding: "12px 16px" }}>
              <button type="button" onClick={agregarLinea}
                style={{
                  background: "none", border: "1px dashed var(--border-card)",
                  borderRadius: 6, padding: "7px 14px",
                  color: "var(--text-secondary)", cursor: "pointer", fontSize: 13,
                  transition: "all 0.2s",
                }}
                onMouseOver={(e) => { e.currentTarget.style.borderColor = "#f97316"; e.currentTarget.style.color = "#f97316"; }}
                onMouseOut={(e)  => { e.currentTarget.style.borderColor = "var(--border-card)"; e.currentTarget.style.color = "var(--text-secondary)"; }}
              >
                <i className="fa-solid fa-plus" style={{ marginRight: 5 }} />{t("asientos.agregarLinea")}
              </button>
            </div>
          </div>
        </div>

        {/* ── Barra inferior: estado de balance + acciones ── */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between",
          flexWrap: "wrap", gap: 12 }}>
          <div style={{
            display: "flex", alignItems: "center", gap: 12, padding: "10px 16px", borderRadius: 10,
            background: ok ? "rgba(74,222,128,0.09)" : totalD === 0 ? "var(--bg-card)" : "rgba(239,68,68,0.09)",
            border: `1px solid ${ok ? "rgba(74,222,128,0.3)" : totalD === 0 ? "var(--border-card)" : "rgba(239,68,68,0.3)"}`,
          }}>
            <div style={{ width: 34, height: 34, borderRadius: 9, flexShrink: 0,
              background: ok ? "rgba(74,222,128,0.15)" : "transparent",
              display: "flex", alignItems: "center", justifyContent: "center" }}>
              <i className={`fa-solid ${ok ? "fa-shield-halved" : totalD === 0 ? "fa-circle-info" : "fa-triangle-exclamation"}`}
                style={{ color: ok ? "#4ade80" : totalD === 0 ? "var(--text-secondary)" : "#f87171", fontSize: 15 }} />
            </div>
            <div>
              <div style={{ fontWeight: 700, fontSize: 13.5,
                color: ok ? "#4ade80" : totalD === 0 ? "var(--text-secondary)" : "#f87171" }}>
                {ok ? t("asientos.balanceado")
                  : totalD === 0 ? t("asientos.ingresaMontos")
                  : t("asientos.noBalanceado")}
              </div>
              {ok ? (
                <div style={{ fontSize: 12, color: "var(--text-muted)" }}>{t("asientos.listoGuardar")}</div>
              ) : totalD > 0 && (
                <div style={{ fontSize: 12, color: "var(--text-muted)" }}>
                  {!crcOk && `₡ ${t("asientos.noBalanceadoDiff")}${fmt(diff)}`}
                  {!crcOk && hasUsd && !usdOk && "  ·  "}
                  {hasUsd && !usdOk && `$ ${t("asientos.noBalanceadoDiff")}${fmtUSD(diffUsd)}`}
                </div>
              )}
            </div>
          </div>

          <div className={styles.actions}>
            <Link to="/finanzas/asientos" className={`${styles.btn} ${styles["btn-clean-form"]}`}
              style={{ textDecoration: "none" }}>
              {t("common.cancelar")}
            </Link>
            <button type="submit" disabled={saving}
              className={`${styles.btn} ${styles["btn-save"]}`}
              style={{ opacity: saving ? 0.6 : 1 }}>
              {saving
                ? <><i className="fa-solid fa-circle-notch fa-spin" /> {t("catalogo.guardando")}</>
                : <><i className="fa-solid fa-save" /> {t("asientos.guardarAsiento")}</>
              }
            </button>
          </div>
        </div>
      </form>
    </>
  );
}
